import _ from "lodash"
import { useContext, useEffect } from "react"
import { Field, FastField, useFormikContext } from "formik"

import { FormContext } from "../../context/formContext"
import { RadioButton } from "primereact/radiobutton"

import styles from "../../styles/Form.module.css"

import fieldVisibility from "../../helpers/fieldVisibility"
import { classNames } from "primereact/utils"

function RegularFieldOutput(props) {
  const { field, isRequired, block, index } = props
  const form = useContext(FormContext)
  const formik = useFormikContext()
  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  let visibility = fieldVisibility(field.field_visibility)
  if (field.field_name === "participant_email_confirm" && _.isEmpty(form)) {
    visibility = "none"
  }

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: visibility,
      }}
    >
      <label className={styles.label} style={{ marginBottom: 5 }}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={classNames(styles.radioButtonContainer, styles.value)}>
        {_.map(field.field_options, (option) => {
          return (
            <div key={option.value}>
              <Field name={field.field_name}>
                {({ field: formikField, meta }) => {
                  return (
                    <div className={styles.radioButtonOption}>
                      <RadioButton
                        disabled={isDisabled}
                        inputId={`${field.field_name}_${option.value}`}
                        name={formikField.name}
                        value={option.value}
                        onChange={formikField.onChange}
                        checked={formikField.value === option.value}
                      />
                      <label
                        htmlFor={`${field.field_name}_${option.value}`}
                        style={{ marginLeft: 7 }}
                      >
                        {option.label}
                      </label>
                    </div>
                  )
                }}
              </Field>
            </div>
          )
        })}
        {formik.touched[field.field_name] && formik.errors[field.field_name] && (
          <div className={styles.errFieldMsg}>{formik.errors[field.field_name]}</div>
        )}
      </div>
    </div>
  )
}

function MultiFieldOutput(props) {
  const { field, isRequired, index, mfFieldName, mfIndex } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`
  const formik = useFormikContext()

  // Manually get the error status from the nested objects
  const fieldError = _.get(formik.errors, `${mfFieldName}[${mfIndex}].${field.field_name}`)

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>

      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={classNames(styles.radioButtonContainer, styles.radioButtonContainerExtended)}>
        {_.map(field.field_options, (option) => {
          return (
            <div key={option.value} className="field-radiobutton">
              <FastField name={fieldSlug}>
                {({ field: fastField, form: formik }) => {
                  return (
                    <>
                      <RadioButton
                        inputId={`${field.field_name}_${option.value}`}
                        name={fastField.name}
                        value={option.value}
                        onChange={fastField.onChange}
                        checked={fastField.value === option.value}
                      />
                      <label
                        htmlFor={`${field.field_name}_${option.value}`}
                        style={{ marginLeft: 7 }}
                      >
                        {option.label}
                      </label>
                    </>
                  )
                }}
              </FastField>
            </div>
          )
        })}
      </div>
      {
        fieldError && (
          <div className={styles.errFieldMsg}>
            {fieldError}
          </div>
        )
      }
    </div >
  )
}

function NestedMultiFieldOutput(props) {
  const {
    field,
    isRequired,
    index,
    mfFieldName,
    mfIndex,
    nestedMfFieldName,
    nestedMfIndex,
  } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${nestedMfFieldName}.${nestedMfIndex}.${field.field_name}`
  const formik = useFormikContext()

  // Manually get the error status from the nested objects
  const fieldError = _.get(formik.errors, `${mfFieldName}[${mfIndex}].${field.field_name}`)

  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>

      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={classNames(styles.radioButtonContainer, styles.radioButtonContainerExtended)}>
        {_.map(field.field_options, (option) => {
          return (
            <div key={option.value} className="field-radiobutton">
              <FastField name={fieldSlug}>
                {({ field: fastField, form: formik }) => {
                  return (
                    <>
                      <RadioButton
                        inputId={`${nestedMfFieldName}_${field.field_name}_${option.value}`}
                        name={fastField.name}
                        value={option.value}
                        onChange={fastField.onChange}
                        checked={fastField.value === option.value}
                      />
                      <label
                        htmlFor={`${field.field_name}_${option.value}`}
                        style={{ marginLeft: 7 }}
                      >
                        {option.label}
                      </label>
                    </>
                  )
                }}
              </FastField>
            </div>
          )
        })}
      </div>
      {
        fieldError && (
          <div className={styles.errFieldMsg}>
            {fieldError}
          </div>
        )
      }
    </div >
  )
}

export default function TypeRadio(props) {
  const checkIfRequired = () => {
    try {
      return JSON.parse(props.field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }
  const isRequired = checkIfRequired()
  switch (props.type) {
    case "regular-field":
      const regFieldProps = {
        field: props.field,
        isRequired,
        block: props.block,
        index: props.index,
      }
      return <RegularFieldOutput {...regFieldProps} />
    case "multi-field":
      const multiFieldProps = {
        field: props.field,
        isRequired,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
      }
      return <MultiFieldOutput {...multiFieldProps} />
    case "nested-multi-field":
      const nestedMultiFieldProps = {
        field: props.field,
        isRequired,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
        nestedMfFieldName: props.nestedMfFieldName,
        nestedMfIndex: props.nestedMfIndex,
      }
      return <NestedMultiFieldOutput {...nestedMultiFieldProps} />

    default:
      return null
  }
}
